import { useState } from "react";
import { toast } from "sonner";
import { ClipboardCopy, ExternalLink, Loader2, Share2 } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { copyImageToClipboard, shareImageNative, isIOS } from "./clipboardImage";

/**
 * Send to Paperless — robust across desktop, Android, and iOS.
 *
 * Flow on each platform:
 *   • Desktop / Android Chrome: tap "העתק" → image lands in OS clipboard
 *     via `navigator.clipboard.write([new ClipboardItem(...)])`. Then tap
 *     "שלח" to open the empty Paperless chat and Ctrl/Cmd+V → Send.
 *
 *   • iOS Safari: WebKit doesn't reliably expose `clipboard.write(image)`
 *     to the WhatsApp paste box. So on iOS the button label flips to
 *     "שתף" and the click goes STRAIGHT to `navigator.share` — the iOS
 *     share sheet comes up with the photo already attached. Two extra
 *     taps (WhatsApp → Paperless contact) and the real image is sent.
 *
 *   • Whichever platform fails the clipboard path falls through to a final
 *     plain-text URL copy + toast warning so the user never gets stuck.
 */
export function PaperlessSend({ imageUrl, businessName, waHref, disabled, testIdBase }) {
  const [busy, setBusy] = useState(false);
  const noImage = !imageUrl;
  const ios = isIOS();

  // ---- iOS path: native share with attached file ----
  const onShare = async () => {
    if (noImage) { toast.error("צריך תמונה כדי לשלוח"); return; }
    setBusy(true);
    try {
      const filename = `${(businessName || "receipt").replace(/[^\w\u0590-\u05ff\- ]/g, "")}.jpg`;
      const ok = await shareImageNative(imageUrl, filename);
      if (!ok) {
        const copied = await copyImageToClipboard(imageUrl);
        if (copied) {
          toast.success("התמונה הועתקה — פתח וואטסאפ והדבק");
          window.open(waHref, "_blank", "noopener,noreferrer");
        } else {
          toast.error("שיתוף לא נתמך כאן — לחיצה ארוכה על התמונה ושמור");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  // ---- Desktop / Android path: copy to clipboard ----
  const onCopy = async () => {
    if (noImage) { toast.error("צריך תמונה כדי להעתיק"); return; }
    setBusy(true);
    try {
      const ok = await copyImageToClipboard(imageUrl);
      if (ok) {
        toast.success("התמונה הועתקה — פתח וואטסאפ והדבק עם Ctrl/Cmd+V");
      } else {
        try {
          await navigator.clipboard.writeText(imageUrl);
          toast.warning("הדפדפן לא תומך בהעתקת תמונה — הועתק הקישור");
        } catch {
          toast.error("העתקה נכשלה — נסה שוב או השתמש בשיתוף הנייד");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  if (ios) {
    // iOS gets THREE actions: Copy, Share (with attached file), Open chat.
    // The user can choose: Copy → Open is one flow; Share is the alternate.
    return (
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onCopy}
          disabled={disabled || noImage || busy}
          className={`inline-flex items-center justify-center gap-1 px-2.5 py-2 rounded-lg bg-stone-800 border border-white/15 text-stone-100 hover:bg-pink-500/15 hover:border-pink-500/40 hover:text-pink-200 transition text-xs font-bold ${disabled || noImage ? "opacity-30 pointer-events-none" : ""}`}
          data-testid={`${testIdBase}-copy`}
          title="העתק את התמונה — תוכל להדביק בוואטסאפ"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCopy className="h-3.5 w-3.5" />}
          העתק
        </button>
        <button
          type="button"
          onClick={onShare}
          disabled={disabled || noImage || busy}
          className={`inline-flex items-center justify-center gap-1 px-2.5 py-2 rounded-lg bg-stone-800 border border-white/15 text-stone-100 hover:bg-sky-500/15 hover:border-sky-500/40 hover:text-sky-200 transition text-xs font-bold ${disabled || noImage ? "opacity-30 pointer-events-none" : ""}`}
          data-testid={`${testIdBase}-share`}
          title="שיתוף של האייפון עם התמונה מצורפת"
        >
          <Share2 className="h-3.5 w-3.5" />
          שתף
        </button>
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => { if (disabled) e.preventDefault(); }}
          className={`inline-flex items-center gap-1 px-2.5 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 hover:text-emerald-200 transition text-xs font-bold ${disabled ? "opacity-30 pointer-events-none" : ""}`}
          data-testid={`${testIdBase}-open`}
          title="פותח את הצ'אט עם פפרלס"
        >
          <WhatsAppIcon size={14} mono />
          שלח
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={onCopy}
        disabled={disabled || noImage || busy}
        className={`inline-flex items-center justify-center gap-1 px-2.5 py-2 rounded-lg bg-stone-800/60 border border-white/15 text-stone-100 hover:bg-pink-500/15 hover:border-pink-500/40 hover:text-pink-200 transition text-xs font-bold ${disabled || noImage ? "opacity-30 pointer-events-none" : ""}`}
        data-testid={`${testIdBase}-copy`}
        title="העתק את התמונה — תוכל להדביק בוואטסאפ עם Ctrl/Cmd+V"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCopy className="h-3.5 w-3.5" />}
        העתק
      </button>
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => { if (disabled) e.preventDefault(); }}
        className={`inline-flex items-center gap-1 px-2.5 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 hover:text-emerald-200 transition text-xs font-bold ${disabled ? "opacity-30 pointer-events-none" : ""}`}
        data-testid={`${testIdBase}-open`}
        title="פותח את הצ'אט עם פפרלס"
      >
        <WhatsAppIcon size={14} mono />
        שלח
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
